import { useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { Download, FileText } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { bytesToBlob } from '@/lib/utils';

const SAMPLE = `# Quarterly Report

## Highlights

- Revenue grew **18%** YoY
- Active users crossed 1.2 million
- New product line launched on April 15

## Next quarter

We will focus on:

1. Onboarding flow improvements
2. Activation experiments in tier-2 cities
3. Backend reliability investments
`;

interface Token {
  text: string;
  size: number;
  bold?: boolean;
  italic?: boolean;
  newline?: number;
  bullet?: string;
}

/** Convert sanitized markdown HTML into a flat token stream. */
function tokenize(html: string): Token[] {
  const div = document.createElement('div');
  div.innerHTML = html;
  const tokens: Token[] = [];

  function walk(el: Node, ctx: { size: number; bold?: boolean; italic?: boolean; bullet?: string } = { size: 11 }) {
    el.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        if (text.trim().length > 0) {
          tokens.push({ text, size: ctx.size, bold: ctx.bold, italic: ctx.italic, bullet: ctx.bullet });
          ctx.bullet = undefined;
        }
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const tag = (node as Element).tagName.toLowerCase();
      const inherit = { ...ctx };
      if (tag === 'h1') inherit.size = 24;
      if (tag === 'h2') inherit.size = 19;
      if (tag === 'h3') inherit.size = 15;
      if (tag === 'strong' || tag === 'b') inherit.bold = true;
      if (tag === 'em' || tag === 'i') inherit.italic = true;
      if (tag === 'li') inherit.bullet = '•';
      walk(node, inherit);

      if (['h1', 'h2', 'h3', 'p', 'li', 'blockquote', 'pre'].includes(tag)) {
        tokens.push({ text: '', size: 11, newline: tag === 'h1' ? 2 : 1 });
      }
    });
  }
  walk(div);
  return tokens;
}

export function MarkdownToPdf() {
  const [md, setMd] = useState(SAMPLE);
  const [working, setWorking] = useState(false);

  async function buildPdf() {
    setWorking(true);
    try {
      const html = DOMPurify.sanitize(marked.parse(md, { gfm: true }) as string);
      const tokens = tokenize(html);

      const doc = await PDFDocument.create();
      const font = await doc.embedFont(StandardFonts.Helvetica);
      const bold = await doc.embedFont(StandardFonts.HelveticaBold);
      const italic = await doc.embedFont(StandardFonts.HelveticaOblique);
      const boldItalic = await doc.embedFont(StandardFonts.HelveticaBoldOblique);

      let page = doc.addPage([595, 842]);
      const margin = 50;
      const maxWidth = 595 - margin * 2;
      let y = 842 - margin;

      function pickFont(t: Token) {
        if (t.bold && t.italic) return boldItalic;
        if (t.bold) return bold;
        if (t.italic) return italic;
        return font;
      }

      function nextLine(extra = 0) {
        y -= 16 + extra;
        if (y < margin) {
          page = doc.addPage([595, 842]);
          y = 842 - margin;
        }
      }

      for (const t of tokens) {
        if (t.newline) {
          nextLine(t.newline > 1 ? 4 : 0);
          continue;
        }
        const f = pickFont(t);
        let prefix = '';
        if (t.bullet) prefix = '  •  ';
        const words = (prefix + t.text).split(/\s+/);
        let line = '';
        for (const w of words) {
          const test = line ? `${line} ${w}` : w;
          const width = f.widthOfTextAtSize(test, t.size);
          if (width > maxWidth && line) {
            page.drawText(line, { x: margin, y, font: f, size: t.size, color: rgb(0.1, 0.1, 0.12) });
            nextLine(t.size > 14 ? 4 : 0);
            line = w;
          } else {
            line = test;
          }
        }
        if (line) {
          page.drawText(line, { x: margin, y, font: f, size: t.size, color: rgb(0.1, 0.1, 0.12) });
          if (t.size >= 19) nextLine(6);
          else nextLine(t.size > 14 ? 4 : 0);
        }
      }

      const bytes = await doc.save();
      saveAs(bytesToBlob(bytes), 'document.pdf');
      toast.success('PDF saved');
    } catch (e) {
      toast.error('Failed to build PDF');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="md-to-pdf"
        title="Markdown → PDF"
        description="Render markdown into a printable PDF — paragraphs, headings, bold, italic, and bullet lists."
        actions={
          <Button size="sm" variant="primary" loading={working} onClick={buildPdf}>
            <Download size={11} />
            Export PDF
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
            Markdown
          </div>
          <Textarea
            mono
            value={md}
            onChange={(e) => setMd(e.target.value)}
            className="!border-0 !rounded-t-none min-h-[480px] !bg-transparent focus-visible:!ring-0"
          />
        </Card>
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted flex items-center gap-1.5">
            <FileText size={11} />
            Preview
          </div>
          <div
            className="prose-uos p-5 overflow-auto min-h-[480px] max-h-[600px]"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(md, { gfm: true }) as string) }}
          />
        </Card>
      </div>
    </div>
  );
}

export default MarkdownToPdf;
